import { useEffect, useMemo } from 'react';
import AllOrganisationTable from './organisation-tables/all-table';
import { DataTableSkeleton } from '@/components/ui/table/data-table-skeleton';
import { useGetAllOrganisations } from '@/services/organisations/queries';
import type { Organisation, Filter } from '@/services/types';
import { useQueryState } from 'nuqs';
import { searchParams } from '@/lib/searchparams';
import { useAuthStore } from '@/store/auth';

type AllOrgProps = {
  onCountUpdate: (value: number) => void;
};

const AllOrg = ({ onCountUpdate }: AllOrgProps) => {
  // Use nuqs for all search parameter values
  const [page, setPage] = useQueryState('page', searchParams.page);
  const [limit] = useQueryState('limit', searchParams.limit);
  const [search] = useQueryState('q', searchParams.q);
  const [sort] = useQueryState('sort', searchParams.sort);
  const [verified] = useQueryState('verified', searchParams.verified);

  useEffect(() => {
    if ((search || verified) && page) {
      setPage(1);
    }
  }, [search, verified]);

  const { context } = useAuthStore();

  // Memoize filters to prevent unnecessary re-renders
  const filters: Filter = useMemo(() => ({
    page: page || 1,
    limit: limit || 20,
    ...(search && { search }),
    ...(sort && { sort }),
    // Only include verified filter for workspace users
    ...(context === 'workspace' && verified && verified !== 'all' && { verified: verified === 'true' })
  }), [page, limit, search, sort, verified, context]);

  const { data, isLoading, error } = useGetAllOrganisations(filters, {
    refetchOnWindowFocus: true,
    refetchOnMount: true
  });

  // Memoize data arrays to prevent unnecessary re-renders
  const organisations: Organisation[] = useMemo(() => data?.data || [], [data?.data]);
  const totalDocs = useMemo(() => data?.pagination?.totalDocs ?? 0, [data?.pagination?.totalDocs]);

  useEffect(() => {
    if (totalDocs !== undefined) {
      onCountUpdate(totalDocs);
    }
  }, [totalDocs, onCountUpdate]);

  if (isLoading) {
    return <DataTableSkeleton />;
  }

  if (error) {
    return (
      <div className="flex items-center justify-center p-8">
        <p className="text-red-500">Failed to load organisations</p>
      </div>
    );
  }

  return (
    <AllOrganisationTable
      data={organisations}
      totalData={totalDocs}
    />
  );
};

export default AllOrg;
